import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Feature, Point, Polygon } from 'geojson';
import type DateFilters from '../../../common-components/date-filter/date-filter.model';
import type { LocationMessage } from '../../../models/vessels/internal-ais-api-v3.model';
import {
  getDefaultValues,
  type HistoricVesselsFormValues,
} from './historic-area-search-form/historic-area-search-form-values';

interface HistoricVesselsState {
  historicVessels: LocationMessage[] | null;
  historicVesselsFormValues: HistoricVesselsFormValues;
  loading: boolean;
  error: boolean;
  token: string | undefined;
  searchInitiated: boolean;
  vesselTypeDefaultColouring: boolean;
}

export const HISTORIC_VESSELS_INITIAL_STATE: HistoricVesselsState = {
  historicVessels: null,
  historicVesselsFormValues: getDefaultValues(),
  loading: false,
  error: false,
  token: undefined,
  searchInitiated: false,
  vesselTypeDefaultColouring: false,
};

const historicVesselsSlice = createSlice({
  name: 'historicVessels',
  initialState: HISTORIC_VESSELS_INITIAL_STATE,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setHistoricVessels: (
      state,
      action: PayloadAction<LocationMessage[] | null>
    ) => {
      state.historicVessels = action.payload;
      state.loading = false;
      state.error = false;
    },
    setHistoricVesselsFormValues: (
      state,
      action: PayloadAction<Partial<HistoricVesselsFormValues>>
    ) => {
      state.historicVesselsFormValues = {
        ...state.historicVesselsFormValues,
        ...action.payload,
      };
    },
    setDates: (state, action: PayloadAction<DateFilters>) => {
      state.historicVesselsFormValues = {
        ...state.historicVesselsFormValues,
        dateTimeRange: action.payload,
      };
    },
    setSearchArea: (
      state,
      action: PayloadAction<Feature<Point | Polygon> | null>
    ) => {
      state.historicVesselsFormValues = {
        ...state.historicVesselsFormValues,
        searchArea: action.payload,
      };
    },
    appendHistoricVessels: (
      state,
      action: PayloadAction<LocationMessage[]>
    ) => {
      const newData = action.payload;
      if (newData) {
        // Retain all data points — deduplication happens at render/display time
        // so that the full dataset is available for future playback/scrubbing.
        state.historicVessels = [...(state.historicVessels || []), ...newData];
      }
    },
    setError: (state, action: PayloadAction<boolean>) => {
      state.error = action.payload;
    },
    setToken(state, action: PayloadAction<string | undefined>) {
      state.token = action.payload;
    },
    setSearchInitiated(state, action: PayloadAction<boolean>) {
      state.searchInitiated = action.payload;
    },
    setHistoricVesselsColouring(state, action: PayloadAction<boolean>) {
      state.vesselTypeDefaultColouring = action.payload;
    },
  },
});

export const {
  setLoading,
  setHistoricVessels,
  appendHistoricVessels,
  setHistoricVesselsFormValues,
  setError,
  setDates,
  setSearchArea,
  setToken,
  setSearchInitiated,
  setHistoricVesselsColouring,
} = historicVesselsSlice.actions;

export default historicVesselsSlice.reducer;
